import { CircleAlert } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { type SheetNotReady } from '../errors';

/**
 * "Chưa mở được sổ" — mọi lý do còn chặn, mỗi lý do một nút đi sửa.
 *
 * Người dùng 2026-10-07: "Nút mở sổ luôn được active và thông báo hội thoại các
 * lỗi đang gặp phải cần phải sửa để mở sổ". Lý do lấy từ **server**
 * (`409 DECISION_SHEET_NOT_READY`), không tự đoán ở web: điều kiện mở gồm cả
 * link "Trả lời" còn hiệu lực, thứ trang sổ không giữ sẵn.
 */
export function OpenProblemsDialog({
  problems,
  canEdit,
  onAddQuestions,
  onChooseAnswerers,
  onClose,
}: {
  readonly problems: SheetNotReady;
  readonly canEdit: boolean;
  readonly onAddQuestions: () => void;
  readonly onChooseAnswerers: () => void;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const items: { id: string; text: string; action?: { label: string; run: () => void } }[] = [];
  if (problems.missingQuestions) {
    items.push({
      id: 'questions',
      text: t('decisionSheet.openProblems.noQuestions'),
      ...(canEdit
        ? { action: { label: t('decisionSheet.openProblems.addQuestions'), run: onAddQuestions } }
        : {}),
    });
  }
  if (problems.missingAnswerers) {
    items.push({
      id: 'answerers',
      text: t('decisionSheet.openProblems.noAnswerers'),
      action: { label: t('decisionSheet.answerers.action'), run: onChooseAnswerers },
    });
  }

  return (
    <Dialog label={t('decisionSheet.openProblems.title')} onClose={onClose}>
      <h2 className="text-lg font-semibold">{t('decisionSheet.openProblems.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('decisionSheet.openProblems.intro')}</p>
      <ul className="flex flex-col gap-2" data-testid="open-problems">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex flex-col gap-2 rounded-xl border border-rag-amber/40 bg-rag-amber/5 p-3 sm:flex-row sm:items-center"
            data-testid={`open-problem-${item.id}`}
          >
            <span className="flex min-w-0 flex-1 items-start gap-2 text-sm">
              <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-rag-amber" />
              <span className="[overflow-wrap:anywhere]">{item.text}</span>
            </span>
            {item.action ? (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 self-start sm:self-auto"
                onClick={() => {
                  onClose();
                  item.action!.run();
                }}
              >
                {item.action.label}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
